import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AcsButton, AcsCard, AcsInput } from '../components';
import { useI18n } from '../i18n/I18nProvider';
import { api } from '../api/client';

/**
 * Generazione degli export (§15).
 *
 * La finalità è la prima scelta, non un filtro accessorio: un export senza
 * finalità dichiarata produrrebbe il file indistinto che la specifica vieta.
 * Accanto a ogni scelta è spiegato chi entra nel file e chi resta fuori, così
 * l'operatore non deve fidarsi.
 */

const PURPOSES = ['EMAIL_MARKETING', 'DOCUMENTATION_REQUEST', 'COMMERCIAL_CALLBACK'] as const;
type ExportPurpose = (typeof PURPOSES)[number];

export function ExportPanel({ eventId }: { eventId: string }) {
  const { t } = useI18n();
  const [purpose, setPurpose] = useState<ExportPurpose>('DOCUMENTATION_REQUEST');
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<{ rowCount: number; filename: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const download = useMutation({
    mutationFn: () => api.downloadExport(eventId, purpose, reason.trim()),
    onSuccess: ({ blob, filename, rowCount }) => {
      // Il file non viene mai scritto sul server: si materializza qui e
      // l'oggetto URL viene revocato subito dopo, per non lasciarne una copia
      // raggiungibile nella pagina.
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      setResult({ rowCount, filename });
      setError(null);
    },
    onError: (cause: Error) => setError(cause.message),
  });

  return (
    <AcsCard>
      <h2 style={{ marginTop: 0 }}>{t.exports.title}</h2>
      <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.exports.purposeExplain}</p>

      <label className="acs-field">
        <span className="acs-field__label">{t.exports.purpose}</span>
        <select
          className="acs-input"
          value={purpose}
          onChange={(event) => {
            setPurpose(event.target.value as ExportPurpose);
            setResult(null);
          }}
        >
          {PURPOSES.map((value) => (
            <option key={value} value={value}>
              {t.exports.purposes[value]}
            </option>
          ))}
        </select>
      </label>

      {/* Dice esattamente chi entra nel file e chi no, prima di generarlo. */}
      <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.exports.purposeHints[purpose]}</p>

      <AcsInput
        label={t.exports.reason}
        placeholder={t.exports.reasonPlaceholder}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        autoComplete="off"
      />

      {error ? (
        <p className="acs-field__error" role="alert">
          {error}
        </p>
      ) : null}

      <AcsButton disabled={download.isPending} onClick={() => download.mutate()}>
        {download.isPending ? t.exports.generating : t.exports.download}
      </AcsButton>

      {result ? (
        <div role="status" style={{ marginTop: 'var(--acs-space-4)' }}>
          <p style={{ margin: 0 }}>
            <strong>{t.exports.done}</strong> — {result.filename} · {result.rowCount}{' '}
            {t.exports.rows}
          </p>
          {result.rowCount === 0 ? <p style={{ margin: 0 }}>{t.exports.empty}</p> : null}

          {/*
            §15 chiede di ricordare esplicitamente all'operatore che il file
            scaricato è una copia di dati personali, che da qui in poi vive
            fuori dal sistema e dai suoi controlli.
          */}
          <p
            style={{
              background: 'var(--acs-color-highlight)',
              color: 'var(--acs-color-highlight-contrast)',
              padding: 'var(--acs-space-3)',
              borderRadius: 'var(--acs-radius-md)',
            }}
          >
            {t.exports.copyWarning}
          </p>
          <p style={{ color: 'var(--acs-color-text-muted)', margin: 0 }}>{t.exports.audited}</p>
        </div>
      ) : null}
    </AcsCard>
  );
}
